import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Button, FileUpload, FormRow, TagsInput } from '../controls';

const defaultLinks = {
  instagram: '',
  twitter: '',
  facebook: '',
  web: '',
};

function buildDefaultValues(vendor) {
  const values = {
    vendorName: vendor ? vendor.vendorName || '' : '',
    vendorContact: vendor ? vendor.vendorContact || '' : '',
    vendorDescription: vendor ? vendor.vendorDescription || '' : '',
    links: vendor ? vendor.links || defaultLinks : defaultLinks,
    category: null,
    subcategory: null,
  };

  if (vendor?.category) {
    values.category = vendor.category;
  }
  if (vendor?.subcategory) {
    values.subcategory = vendor.subcategory;
  }

  return values;
}

export function VendorForm({
  vendor,
  vendorCategories,
  onFileUpload,
  onFileDelete,
  handleSave,
  formRef = undefined,
  className = undefined,
  isPending = false,
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(vendor),
    mode: 'onSubmit',
  });

  const vendorId = vendor?.vendorId;

  useEffect(() => {
    reset(buildDefaultValues(vendor));
  }, [vendorId, reset]);

  const saveVendor = (values) => {
    const category = values.category ? values.category : '';
    const subcategory = values.subcategory ? values.subcategory : '';
    const payload = {
      ...values,
      category,
      subcategory,
      vendorId: vendor?.vendorId,
    };

    handleSave(payload);
  };

  const categories = vendorCategories.categories || [];
  const subcategories = vendorCategories.subcategories || [];

  const fileList = [];

  if (vendor?.photoURL) {
    fileList.push({
      uid: '-1',
      name: vendor.vendorPhoto,
      url: vendor.photoURL,
      status: 'done',
    });
  }

  return (
    <form
      ref={formRef}
      className={cn(className)}
      onSubmit={handleSubmit(saveVendor)}
      noValidate
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="min-w-0 flex-1">
          <FormRow label="Vendor Name" error={errors.vendorName?.message}>
            <input
              type="text"
              placeholder="Name"
              {...register('vendorName', {
                required: 'Vendor name is required',
              })}
            />
          </FormRow>
          <FormRow label="Contact">
            <input
              type="text"
              placeholder="Contact"
              {...register('vendorContact')}
            />
          </FormRow>
          <FormRow label="Category" error={errors.category?.message}>
            <Controller
              name="category"
              control={control}
              rules={{ required: 'Category is required' }}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a category"
                  options={categories}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
          <FormRow label="Subcategory">
            <Controller
              name="subcategory"
              control={control}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a subcategory"
                  options={subcategories}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
        </div>
        <div className="w-full shrink-0 sm:w-48 md:w-56">
          <FormRow>
            <div className="w-full">
              <FileUpload
                fileList={fileList}
                onFileUpload={onFileUpload}
                onFileDelete={onFileDelete}
              />
            </div>
          </FormRow>
        </div>
      </div>
      <FormRow label="Description">
        <textarea
          placeholder="Vendor Description..."
          {...register('vendorDescription')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fas', 'globe']} />}>
        <input type="text" placeholder="Website" {...register('links.web')} />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'facebook']} />}>
        <input
          type="text"
          placeholder="Facebook"
          {...register('links.facebook')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'twitter']} />}>
        <input
          type="text"
          placeholder="Twitter"
          {...register('links.twitter')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'instagram']} />}>
        <input
          type="text"
          placeholder="Instagram"
          {...register('links.instagram')}
        />
      </FormRow>
      <div className="mt-8 flex justify-end border-t border-black/[0.08] pt-6 pb-2">
        <Button type="submit" mode="dark" disabled={isPending}>
          Save Vendor
        </Button>
      </div>
    </form>
  );
}
